/*
 * Shopify Wishlist Module
 * localStorage + backend API async to customer metafield
 */

const Wishlist = {
  GUEST_KEY: "shopify_wishlist_guest",
  MEMBER_KEY: "shopify_wishlist_member",

  // ====== setting ======
  API_BASE: "https://wishlistapi-z5xe.onrender.com",
  API_SECRET: "musashiit",

  // ====== log in status ======
  isLoggedIn() {
    return (
      document.body.classList.contains("customer-logged-in") ||
      (typeof window.__wishlistCustomerId !== "undefined" &&
        window.__wishlistCustomerId)
    );
  },

  getCustomerId() {
    return window.__wishlistCustomerId || null;
  },

  _getKey() {
    return this.isLoggedIn() ? this.MEMBER_KEY : this.GUEST_KEY;
  },

  // ====== localStorage  ======
  getAll() {
    try {
      return JSON.parse(localStorage.getItem(this._getKey())) || [];
    } catch (e) {
      return [];
    }
  },

  _save(items) {
    localStorage.setItem(this._getKey(), JSON.stringify(items));

    // only sync to backend when user is logged in, guest's wishlist is stored in localStorage only
    if (this.isLoggedIn()) {
      this._syncToBackend(items);
    }

    window.dispatchEvent(
      new CustomEvent("wishlist:update", { detail: { items } }),
    );
  },

  // ====== backend API async ======
  _syncTimer: null,
  _pendingItems: null,

  _syncToBackend(items) {
    this._pendingItems = items;
    if (this._syncTimer) clearTimeout(this._syncTimer);
    this._syncTimer = setTimeout(() => {
      this._doSync(this._pendingItems);
      this._syncTimer = null;
    }, 1200);
  },

  async _doSync(items) {
    const customerId = this.getCustomerId();
    if (!customerId) return;

    try {
      const res = await fetch(`${this.API_BASE}/wishlist/${customerId}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "x-api-key": this.API_SECRET,
        },
        body: JSON.stringify({
          items: items.map((item) => ({
            productId: item.pid,
            variantId: item.vid,
            handle: item.handle,
            added: item.added,
          })),
        }),
      });

      if (res.ok) {
      } else {
        console.warn("Wishlist sync failed:", res.status);
      }
    } catch (err) {
      console.error("Wishlist sync error:", err);
    }
  },

  // when log in, get the data from backed and replace the member localStorage
  async _loadFromBackend() {
    const customerId = this.getCustomerId();
    if (!customerId) return;

    try {
      const res = await fetch(`${this.API_BASE}/wishlist/${customerId}`, {
        headers: { "x-api-key": this.API_SECRET },
      });

      if (!res.ok) return;

      const data = await res.json();
      if (
        data.wishlist &&
        Array.isArray(data.wishlist) &&
        data.wishlist.length > 0
      ) {
        const items = data.wishlist.map((item) => ({
          pid: item.productId || item.pid,
          vid: item.variantId || item.vid,
          handle: item.handle,
          added: item.added,
        }));

        localStorage.setItem(this.MEMBER_KEY, JSON.stringify(items));

        window.dispatchEvent(
          new CustomEvent("wishlist:update", { detail: { items } }),
        );
      }
    } catch (err) {
      console.error("Error loading wishlist from backend:", err);
    }
  },

  add(productId, variantId, handle) {
    const items = this.getAll();
    if (items.some((item) => item.pid === productId)) return;
    items.push({
      pid: productId,
      vid: variantId,
      handle: handle,
      added: Date.now(),
    });
    this._save(items);
  },

  remove(productId) {
    const items = this.getAll().filter((item) => item.pid !== productId);
    this._save(items);
  },

  toggle(productId, variantId, handle) {
    if (this.contains(productId)) {
      this.remove(productId);
      return false;
    } else {
      this.add(productId, variantId, handle);
      return true;
    }
  },

  contains(productId) {
    return this.getAll().some((item) => item.pid === productId);
  },

  count() {
    return this.getAll().length;
  },

  clear() {
    this._save([]);
  },

  init() {
    if (this.isLoggedIn()) {
      this._loadFromBackend();
    }
  },
};

function initWishlistButtons() {
  document
    .querySelectorAll("[data-wishlist-btn], [data-wishlist-btn-on-product]")
    .forEach((btn) => {
      if (btn.hasAttribute("data-wishlist-initialized")) {
        updateWishlistButtonState(btn);
        return;
      }

      const productId = Number(btn.dataset.productId);
      const variantId = Number(btn.dataset.variantId);
      const handle = btn.dataset.productHandle;

      btn.setAttribute("data-wishlist-initialized", "true");

      btn.addEventListener("click", (e) => {
        e.preventDefault();
        e.stopPropagation();
        const added = Wishlist.toggle(productId, variantId, handle);
        updateAllWishlistButtonsForProduct(productId);
        if (btn.hasAttribute("data-wishlist-btn-on-product")) {
          btn.classList.add("is-animating");
          setTimeout(() => btn.classList.remove("is-animating"), 300);
        }

        if (added && typeof window.showWishlistToast === "function") {
          window.showWishlistToast(handle);
        }
      });

      updateWishlistButtonState(btn);
      btn.classList.remove("is-loading");
    });
}

function updateWishlistButtonState(btn) {
  const productId = Number(btn.dataset.productId);

  if (btn.hasAttribute("data-wishlist-btn")) {
    if (Wishlist.contains(productId)) {
      btn.classList.add("is-active");
      btn.setAttribute("aria-label", "Remove from wishlist");
      btn.textContent = "Rimuovi dalla lista dei desideri";
    } else {
      btn.classList.remove("is-active");
      btn.setAttribute("aria-label", "Add to wishlist");
      btn.textContent = "AGGIUNGI ALLA LISTA DEI DESIDERI";
    }
  } else if (btn.hasAttribute("data-wishlist-btn-on-product")) {
    if (Wishlist.contains(productId)) {
      btn.classList.add("is-active");
      btn.setAttribute("aria-label", "Remove from wishlist");
    } else {
      btn.classList.remove("is-active");
      btn.setAttribute("aria-label", "Add to wishlist");
    }
  }
}

function updateAllWishlistButtonsForProduct(productId) {
  document
    .querySelectorAll(`[data-product-id="${productId}"]`)
    .forEach((btn) => {
      if (
        btn.hasAttribute("data-wishlist-btn") ||
        btn.hasAttribute("data-wishlist-btn-on-product")
      ) {
        updateWishlistButtonState(btn);
      }
    });
}

function initWishlistDrawer() {
  const toggleBtn = document.querySelector("[data-wishlist-toggle]");
  const drawer = document.getElementById("site-wishlist-sidebar");

  if (!toggleBtn || !drawer) return;

  toggleBtn.addEventListener("click", (e) => {
    e.preventDefault();
    e.stopPropagation();

    const isOpen = drawer.classList.contains("drawer--open");
    if (isOpen) {
      closeWishlistDrawer();
    } else {
      openWishlistDrawer();
    }
  });

  drawer.addEventListener("click", (e) => {
    if (e.target.hasAttribute("data-drawer-overlay")) {
      closeWishlistDrawer();
    }
  });

  const closeBtn = drawer.querySelector("[data-drawer-close]");
  if (closeBtn) {
    closeBtn.addEventListener("click", closeWishlistDrawer);
  }

  document.addEventListener("keydown", (e) => {
    if (e.key === "Escape" && drawer.classList.contains("drawer--open")) {
      closeWishlistDrawer();
    }
  });
}

function openWishlistDrawer() {
  const drawer = document.getElementById("site-wishlist-sidebar");
  if (!drawer) return;
  drawer.classList.add("drawer--open");
  drawer.setAttribute("aria-hidden", "false");
  document.body.classList.add("drawer-open");
  renderWishlistPage();
}

function closeWishlistDrawer() {
  const drawer = document.getElementById("site-wishlist-sidebar");
  if (!drawer) return;
  drawer.classList.remove("drawer--open");
  drawer.setAttribute("aria-hidden", "true");
  document.body.classList.remove("drawer-open");
}

window.reinitWishlistButtons = function () {
  initWishlistButtons();
};

function updateWishlistCount() {
  document.querySelectorAll("[data-wishlist-count]").forEach((el) => {
    const count = Wishlist.count();
    el.textContent = count;
    el.style.display = count > 0 ? "" : "none";
  });
}

async function renderWishlistPage() {
  const container = document.querySelector("[data-wishlist-grid]");
  if (!container) return;

  const items = Wishlist.getAll();
  const emptyMsg = document.querySelector("[data-wishlist-empty]");

  if (items.length === 0) {
    container.style.display = "none";
    if (emptyMsg) {
      emptyMsg.style.display = "block";
      return;
    }
  }

  container.style.display = "";
  if (emptyMsg) emptyMsg.style.display = "none";

  const tpl = document.getElementById("skeleton-template");
  container.innerHTML = tpl.innerHTML;
  // return;
  const cards = [];

  for (const item of items) {
    try {
      const res = await fetch(
        "/products/" + item.handle + "?sections=wishlist-product-card",
      );
      if (!res.ok) {
        console.warn(`Failed to fetch product ${item.handle}: ${res.status}`);
        Wishlist.remove(item.pid);
        continue;
      }
      const data = await res.json();
      const html = data["wishlist-product-card"];
      if (html && html.trim()) {
        cards.push(html);
      } else {
        console.warn(`No HTML returned for product ${item.handle}`);
        Wishlist.remove(item.pid);
      }
    } catch (e) {
      console.error(`Error fetching product ${item.handle}:`, e);
      Wishlist.remove(item.pid);
    }
  }

  if (cards.length === 0) {
    container.style.display = "none";
    if (emptyMsg) {
      emptyMsg.style.display = "block";
    }
    return;
  }

  container.innerHTML = cards.join("");

  container.querySelectorAll("[data-wishlist-remove]").forEach((btn) => {
    btn.addEventListener("click", (e) => {
      e.preventDefault();
      const pid = Number(btn.dataset.wishlistRemove);
      Wishlist.remove(pid);
      const card = document.querySelector(`[data-wishlist-card="${pid}"]`);
      if (card) {
        const section = card.closest(".shopify-section");
        if (section) {
          section.style.opacity = "0";
          section.style.transform = "scale(0.95)";
          setTimeout(() => {
            section.remove();
            if (Wishlist.count() === 0) {
              container.style.display = "none";
              const emptyMsg = document.querySelector("[data-wishlist-empty]");
              if (emptyMsg) emptyMsg.style.display = "block";
            } else {
              const remainingCards =
                container.querySelectorAll(".wishlist-card");
              if (remainingCards.length === 0) {
                container.style.display = "none";
                const emptyMsg = document.querySelector(
                  "[data-wishlist-empty]",
                );
                if (emptyMsg) emptyMsg.style.display = "block";
              }
            }
          }, 300);
        }
      }
      updateWishlistCount();
    });
  });

  container.querySelectorAll(".wishlist-card__atc").forEach((btn) => {
    btn.addEventListener("click", async (e) => {
      e.preventDefault();
      const variantId = btn.dataset.variantId;
      if (!variantId) return;

      const originalText = btn.textContent;
      btn.textContent = "Aggiungendo...";
      btn.disabled = true;

      try {
        const res = await fetch("/cart/add.js", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            items: [{ id: Number(variantId), quantity: 1 }],
          }),
        });

        if (res.ok) {
          btn.textContent = "Aggiunto!";
          setTimeout(() => {
            btn.textContent = originalText;
            btn.disabled = false;
          }, 1500);

          if (typeof window.refreshCart === "function") {
            window.refreshCart();
          } else {
            fetch("/cart.js")
              .then((r) => r.json())
              .then((cart) => {
                document.querySelectorAll("[data-cart-count]").forEach((el) => {
                  el.textContent = cart.item_count;
                  el.classList.remove("hidden");
                  el.setAttribute("aria-hidden", "false");
                });
              });
          }
        }
      } catch (err) {
        btn.textContent = originalText;
        btn.disabled = false;
      }
    });
  });
}

// ====== listener ======
window.addEventListener("wishlist:update", () => {
  updateWishlistCount();
  document
    .querySelectorAll("[data-wishlist-btn], [data-wishlist-btn-on-product]")
    .forEach((btn) => {
      updateWishlistButtonState(btn);
    });
});

// ====== init ======
document.addEventListener("DOMContentLoaded", () => {
  Wishlist.init();
  initWishlistButtons();
  initWishlistDrawer();
  updateWishlistCount();
  renderWishlistPage();
});
// Handle page show event to re-render wishlist page when navigating back with bfcache
window.addEventListener("pageshow", () => {
  initWishlistButtons();
  updateWishlistCount();
  renderWishlistPage();
});
